import type { CSSProperties, ReactNode, ElementType } from 'react';
import { text, type TextVariant, type ColorTextAlias } from '@pv-frontend/design-tokens';
import { vars } from '../../theme/contract.css';

export type FontWeight = 'regular' | 'medium' | 'semibold' | 'bold';
export type Tone = ColorTextAlias;

const fontWeightMap: Record<FontWeight, number> = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
};

export interface TypographyProps {
    children: ReactNode;
    variant?: TextVariant;
    fontWeight?: FontWeight;
    tone?: Tone;
    align?: 'left' | 'right' | 'center' | 'justify';
    /** Truncate to this many lines with an ellipsis (genuine truncation only). */
    numberOfLines?: number;
    /** DOM element override; defaults to `h2` for headings, `p` for body. */
    as?: ElementType;
    testID?: string;
    onPress?: () => void | Promise<void>;
}

const getVariantConfig = (variant: TextVariant) => {
    const prefix = `text-${variant}`;
    return {
        fontSize: text[`${prefix}-font-size` as keyof typeof text].value as number,
        fontWeight: text[`${prefix}-font-weight` as keyof typeof text].value as number,
        lineHeight: text[`${prefix}-font-line-height` as keyof typeof text].value as number,
        letterSpacing: text[`${prefix}-font-letter-spacing` as keyof typeof text].value as number,
    };
};

export function Typography({
    children,
    variant = 'body-md',
    fontWeight,
    tone = 'text',
    align,
    numberOfLines,
    as,
    testID,
    onPress,
}: TypographyProps) {
    const config = getVariantConfig(variant);
    const Element: ElementType = as ?? (variant.startsWith('heading') ? 'h2' : 'p');

    const style: CSSProperties = {
        margin: 0,
        fontFamily: vars.fontFamily.sans,
        fontSize: config.fontSize,
        // Weight prop overrides the variant's default weight.
        fontWeight: fontWeight ? fontWeightMap[fontWeight] : config.fontWeight,
        lineHeight: config.lineHeight ? `${config.lineHeight}px` : undefined,
        letterSpacing: config.letterSpacing,
        color: vars.color[tone],
        textAlign: align,
        cursor: onPress ? 'pointer' : undefined,
        // Single-line truncation needs nowrap + ellipsis: line-clamp never
        // ellipsizes an unbreakable string (emails, flag keys).
        ...(numberOfLines === 1
            ? {
                  whiteSpace: 'nowrap' as const,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
              }
            : numberOfLines
              ? {
                    display: '-webkit-box',
                    WebkitLineClamp: numberOfLines,
                    WebkitBoxOrient: 'vertical' as const,
                    overflow: 'hidden',
                }
              : {}),
    };

    return (
        <Element style={style} data-testid={testID} onClick={onPress}>
            {children}
        </Element>
    );
}
